/**
 * Owns how many of the loaded article cards are visible, and whether the
 * "View More" button shows.
 *
 * Why client side: the first batch differs by viewport (12 desktop / 9 mobile)
 * while Shopify's {% paginate %} size is fixed server side. posts_per_page is
 * kept equal to the desktop batch so the first "Show more" fetches page 2
 * instead of revealing a stray preloaded card.
 *
 * Rules, per viewport, when a filter is selected:
 *  - total below `showAllBelow`            -> show everything, no button
 *  - remainder after the first batch <= `stubMax` -> show everything, no button
 *
 * Usage:
 *   <blog-results data-total="{{ paginate.items }}" data-filtered="true">
 *     <custom-pagination>
 *       <ul id="blog-results"><li class="js-pagination-result">…</li></ul>
 *       <div class="js-pagination"><a class="js-pagination-load-more">View More</a></div>
 *     </custom-pagination>
 *   </blog-results>
 */
if (!customElements.get('blog-results')) {
  const RULES = {
    desktop: { initial: 12, showAllBelow: 21, stubMax: 6 },
    mobile: { initial: 9, showAllBelow: 15, stubMax: 5 },
  };

  const DESKTOP_QUERY = '(min-width: 769px)';

  const CLAMP_TARGETS = '.card__title, .card__excerpt';

  /**
   * -webkit-line-clamp caps the line count but cuts the final line wherever it
   * runs out of horizontal room, which lands mid-word. This trims back to the
   * last whole word that still fits, then appends the ellipsis itself.
   *
   * The allowed line count is read from the computed CSS clamp, so blog.css stays
   * the single source of truth for the per-breakpoint values.
   */
  function clampToWholeWords(el) {
    // The title wraps its text in the card link — trim inside it so the <a> survives.
    const host = el.querySelector('a') || el;
    const full = host.dataset.clampFull || (host.dataset.clampFull = host.textContent.trim());

    const styles = window.getComputedStyle(el);
    const lines = parseInt(styles.webkitLineClamp, 10);
    const lineHeight = parseFloat(styles.lineHeight);
    if (!lines || !lineHeight) return;

    // 1px of slack absorbs sub-pixel line-height rounding.
    const maxHeight = lines * lineHeight + 1;

    host.textContent = full;
    el.classList.add('is-measuring');

    if (el.scrollHeight > maxHeight) {
      const words = full.split(/\s+/);
      let fits = 0;
      let low = 1;
      let high = words.length;

      // Binary search rather than word-by-word: ~5 reflows instead of ~30.
      while (low <= high) {
        const mid = (low + high) >> 1;
        host.textContent = `${words.slice(0, mid).join(' ')}…`;

        if (el.scrollHeight <= maxHeight) {
          fits = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      host.textContent = fits ? `${words.slice(0, fits).join(' ')}…` : full;
    }

    el.classList.remove('is-measuring');
  }

  function debounce(fn, wait) {
    let timer;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  class BlogResults extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('#blog-results') || this.querySelector('ul');
      this.pagination = this.querySelector('.js-pagination');
      this.total = parseInt(this.dataset.total, 10) || 0;
      this.filtered = this.dataset.filtered === 'true';

      this.mediaQuery = window.matchMedia(DESKTOP_QUERY);
      this.handleBreakpoint = this.handleBreakpoint.bind(this);
      this.mediaQuery.addEventListener('change', this.handleBreakpoint);

      // Capture phase: reveal already-loaded cards before custom-pagination's own
      // bubble-phase handler can fire off a request for them.
      this.handleClick = this.handleClick.bind(this);
      this.addEventListener('click', this.handleClick, true);

      if (this.grid) {
        this.observer = new MutationObserver(() => this.handleNewResults());
        this.observer.observe(this.grid, { childList: true });
      }

      // Column width drives where lines wrap, so re-trim on any resize, not just
      // at the breakpoint.
      this.handleResize = debounce(() => this.clampText(), 150);
      window.addEventListener('resize', this.handleResize);

      this.visible = this.initialVisible();
      this.render();

      // Text measured before the webfont lands is measured at the fallback's
      // metrics, which trims the wrong word.
      if (document.fonts) document.fonts.ready.then(() => this.clampText());
    }

    disconnectedCallback() {
      this.mediaQuery.removeEventListener('change', this.handleBreakpoint);
      this.removeEventListener('click', this.handleClick, true);
      window.removeEventListener('resize', this.handleResize);
      if (this.observer) this.observer.disconnect();
    }

    get rules() {
      return this.mediaQuery.matches ? RULES.desktop : RULES.mobile;
    }

    get pageSize() {
      const configured = parseInt(this.dataset.pageSize, 10);
      return configured > 0 ? configured : this.rules.initial;
    }

    get customPagination() {
      return this.querySelector('custom-pagination');
    }

    get items() {
      return this.grid ? Array.from(this.grid.querySelectorAll('.js-pagination-result')) : [];
    }

    initialVisible() {
      const { initial, showAllBelow, stubMax } = this.rules;

      if (this.filtered) {
        // Small enough to show in full.
        if (this.total < showAllBelow) return this.total;
        // Or the leftovers would be too thin a second batch to be worth a click.
        if (this.total - initial <= stubMax) return this.total;
      }

      return initial;
    }

    handleBreakpoint() {
      // Crossing the breakpoint changes every threshold, so re-derive from scratch.
      this.visible = this.initialVisible();
      this.render();
    }

    handleClick(evt) {
      const loadMore = evt.target.closest('.js-pagination-load-more');
      if (!loadMore) return;

      const loaded = this.items.length;
      if (this.visible >= loaded) return;

      // All-articles listing: posts_per_page matches the desktop batch — nothing
      // to reveal locally; let this click fetch the next page.
      if (!this.filtered) return;

      evt.preventDefault();
      evt.stopPropagation();

      this.visible = loaded;
      this.render();
    }

    handleNewResults() {
      // Cards that arrived from a fetch were explicitly asked for — show them.
      this.visible = this.items.length;
      this.render();
    }

    render() {
      this.items.forEach((item, index) => {
        item.hidden = index >= this.visible;
      });

      if (this.pagination) {
        this.pagination.classList.toggle('hidden', this.visible >= this.total);
        this.updatePaginationMessage();
      }

      this.clampText();
    }

    updatePaginationMessage() {
      const message = this.pagination?.querySelector('.js-pagination-message');
      if (!message) return;

      const visible = Math.min(this.visible, this.total);
      const current = message.textContent || '';

      if (/\d+/.test(current)) {
        message.textContent = current.replace(/^\D*\d+/, (match) => match.replace(/\d+/, String(visible)));
      }
    }

    clampText() {
      this.items.forEach((item) => {
        // A hidden card has no layout, so measuring it returns zero. Cards get
        // clamped when they're revealed instead.
        if (item.hidden) return;
        item.querySelectorAll(CLAMP_TARGETS).forEach(clampToWholeWords);
      });
    }
  }

  customElements.define('blog-results', BlogResults);
}
